import { useState, useEffect, useCallback } from "react";
import { AdminShell } from "@/components/AdminShell";
import api from "@/lib/api";

export default function AdminEconomics() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const load = useCallback(() => api.get("/admin/economics", { params: { month } }).then((r) => setData(r.data)).catch(() => {}), [month]);
  useEffect(() => { load(); }, [load]);

  return (
    <AdminShell title="Customer Economics" testId="admin-economics-page">
      <div className="flex items-center gap-3 mb-4">
        <input data-testid="economics-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm" />
        {data?.free_tier?.users > 0 && (
          <span data-testid="free-tier-burn" className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
            Free tier burn: ${data.free_tier.burn} across {data.free_tier.users} customer{data.free_tier.users > 1 ? "s" : ""}
          </span>
        )}
        {data?.totals?.discounts_given > 0 && (
          <span data-testid="econ-discounts-given" className="text-xs text-emerald-800 bg-emerald-50 rounded px-2 py-1">
            🎟 Discounts given: ${data.totals.discounts_given.toFixed(2)}
          </span>
        )}
      </div>
      {data && (
        <div className="border border-gray-200 rounded-md overflow-hidden" data-testid="economics-table">
          <div className="grid grid-cols-[1.5fr_110px_90px_90px_110px_100px_100px] gap-2 px-4 py-2 bg-gray-50 text-[11px] font-bold uppercase text-gray-500">
            <span>Name</span><span>Type</span><span>Plan</span><span>Revenue</span><span>Tokens used</span><span>My cost</span><span>P/L</span>
          </div>
          {data.rows.map((r) => (
            <div key={r.payer_id} data-testid={`econ-row-${r.payer_id}`}
              className={`grid grid-cols-[1.5fr_110px_90px_90px_110px_100px_100px] gap-2 px-4 py-2.5 border-t border-gray-100 text-sm items-center ${r.profit < 0 ? "bg-red-50/60" : ""}`}>
              <span className="font-medium text-gray-900 truncate">{r.name}</span>
              <span className="text-xs text-gray-500">{r.type}</span>
              <span className="text-xs text-gray-500 capitalize flex items-center gap-1 flex-wrap">{r.plan}
                {r.code && (
                  <span data-testid={`econ-code-badge-${r.payer_id}`} title={`code ${r.code} — full price $${Number(r.full_price || 0).toFixed(2)}/mo`}
                    className={`text-[9px] font-bold rounded-full px-1.5 py-px ${r.discount_pct >= 100 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    🎟 {r.discount_pct >= 100 ? "FREE" : `−${r.discount_pct}%`}
                  </span>
                )}
              </span>
              <span>${r.revenue.toFixed(2)}</span>
              <span>{r.tokens.toLocaleString()}</span>
              <span>${r.my_cost.toFixed(4)}</span>
              <span className={`font-semibold ${r.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{r.profit >= 0 ? "+" : "−"}${Math.abs(r.profit).toFixed(2)}{r.profit < 0 && " ⚠"}</span>
            </div>
          ))}
          {data.rows.length === 0 && <div className="px-4 py-8 text-center text-sm text-gray-400">No usage recorded for {data.month} yet.</div>}
          {data.rows.length > 0 && (
            <div data-testid="econ-totals" className="grid grid-cols-[1.5fr_110px_90px_90px_110px_100px_100px] gap-2 px-4 py-2.5 border-t-2 border-gray-200 text-sm font-bold bg-gray-50">
              <span>TOTALS</span><span /><span />
              <span>${data.totals.revenue.toFixed(2)}</span>
              <span>{data.totals.tokens.toLocaleString()}</span>
              <span>${data.totals.my_cost.toFixed(4)}</span>
              <span className={data.totals.profit >= 0 ? "text-emerald-600" : "text-red-600"}>{data.totals.profit >= 0 ? "+" : "−"}${Math.abs(data.totals.profit).toFixed(2)}</span>
            </div>
          )}
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-3">Revenue = subscription price + token packs paid this month. My cost = real provider spend from the usage ledger. Adjust the rate/markup on the Pricing page if margins run thin.</p>
    </AdminShell>
  );
}
